/**
 * LifecycleBanner — Phase 47
 * ===========================
 * Inline readiness card + Activate Monitoring button.
 *
 * Renders ONLY when the shipment is not yet in `active` lifecycle_state. Once
 * a shipment is active, this component returns null to avoid noise on
 * day-to-day operational views.
 *
 * Behaviour:
 *   - Fetches /api/lifecycle/shipments/{id}/readiness on mount.
 *   - Shows progress bar (score), per-item checklist, lifecycle state badge.
 *   - "Activate Monitoring" CTA disabled until readiness === 100% AND state
 *     in ('ready', 'configuration_required'). Hidden entirely once active or closed.
 *   - On activate success, parent gets notified via onActivated(newState).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Warning, CheckCircle, Circle, Lightning, Lock,
  Buildings, UsersThree, Thermometer, Bell, MagicWand,
} from "@phosphor-icons/react";
import ActivationWizard from "@/components/ActivationWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_META = {
  draft:                  { label: "DRAFT",                  color: "text-zinc-300",    bg: "bg-zinc-500/15",   border: "border-zinc-700/50",  hint: "Configuration not yet started." },
  configuration_required: { label: "CONFIGURATION REQUIRED", color: "text-amber-300",   bg: "bg-amber-500/15",  border: "border-amber-700/50", hint: "Alerts will NOT be dispatched until setup is complete." },
  ready:                  { label: "READY",                  color: "text-teal-300",    bg: "bg-teal-500/15",   border: "border-teal-700/50",  hint: "Setup complete — click Activate Monitoring to start dispatch." },
  active:                 { label: "ACTIVE",                 color: "text-emerald-300", bg: "bg-emerald-500/15",border: "border-emerald-700/50", hint: "Monitoring + dispatch enabled." },
  closed:                 { label: "CLOSED",                 color: "text-zinc-400",    bg: "bg-zinc-700/30",   border: "border-zinc-700/50",  hint: "Read-only investigation mode." },
};

const ITEM_ICONS = {
  facilities:    Buildings,
  stakeholders:  UsersThree,
  thresholds:    Thermometer,
  orchestration: Bell,
};

export default function LifecycleBanner({ shipment, onActivated }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const fetchReadiness = useCallback(async () => {
    if (!shipment?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lifecycle/shipments/${shipment.id}/readiness`);
      setReadiness(data);
    } catch (e) {
      // Banner is best-effort; don't toast on every shipment page load failure.
    } finally {
      setLoading(false);
    }
  }, [shipment?.id]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  const state = (readiness?.current_lifecycle_state || shipment?.lifecycle_state || "draft").toLowerCase();
  const meta = STATE_META[state] || STATE_META.draft;

  // Don't render banner for ACTIVE / CLOSED shipments — keeps the page clean
  // once an operator has finished setup.
  if (state === "active" || state === "closed") return null;
  if (!readiness && !loading) return null;

  const score = readiness?.score ?? 0;
  const ready = readiness?.ready === true;

  const onActivate = async () => {
    if (!ready) {
      toast.error("Cannot activate — readiness must be 100%.");
      return;
    }
    setActivating(true);
    const t = toast.loading("Activating shipment monitoring…");
    try {
      const { data } = await axios.post(`${API}/lifecycle/shipments/${shipment.id}/activate`);
      toast.success("Shipment is now ACTIVE — dispatch enabled.", { id: t });
      onActivated?.(data.lifecycle_state);
      fetchReadiness();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed", { id: t });
    } finally {
      setActivating(false);
    }
  };

  return (
    <Card
      className={`p-4 ${meta.bg} border ${meta.border}`}
      data-testid="lifecycle-banner"
    >
      <div className="flex items-start gap-4">
        {/* State + score */}
        <div className="flex-shrink-0 flex flex-col items-center min-w-[110px]">
          <span
            className={`text-[10px] uppercase tracking-wider font-bold ${meta.color} border ${meta.border} rounded px-2 py-0.5`}
            data-testid="lifecycle-state-badge"
          >
            {meta.label}
          </span>
          <div className="mt-3 relative w-16 h-16">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none"
                strokeWidth="3" className="stroke-zinc-800" />
              <circle cx="18" cy="18" r="16" fill="none"
                strokeWidth="3"
                strokeDasharray={`${score} 100`}
                strokeLinecap="round"
                className={ready ? "stroke-emerald-400" : score >= 50 ? "stroke-amber-400" : "stroke-red-400"} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-sm font-bold ${ready ? "text-emerald-300" : "text-zinc-200"}`}
                data-testid="lifecycle-readiness-score">{score}%</span>
            </div>
          </div>
          <span className="mt-1 text-[9px] text-zinc-500 uppercase tracking-wider">Readiness</span>
        </div>

        {/* Hint + checklist */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {state === "configuration_required" && (
              <Warning size={14} weight="duotone" className={meta.color} />
            )}
            {state === "ready" && (
              <CheckCircle size={14} weight="duotone" className={meta.color} />
            )}
            {state === "draft" && (
              <Lock size={14} weight="duotone" className={meta.color} />
            )}
            <p className={`text-xs font-medium ${meta.color}`}>{meta.hint}</p>
          </div>

          {/* Per-item checklist */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {(readiness?.items || []).map((it) => {
              const Icon = ITEM_ICONS[it.key] || Circle;
              return (
                <div key={it.key}
                  className={`flex items-center gap-1.5 text-[11px] ${it.ok ? "text-emerald-300" : "text-zinc-400"}`}
                  data-testid={`lifecycle-item-${it.key}`}
                  title={it.hint}
                >
                  {it.ok ? (
                    <CheckCircle size={12} weight="fill" className="text-emerald-400 flex-shrink-0" />
                  ) : (
                    <Circle size={12} weight="duotone" className="text-zinc-600 flex-shrink-0" />
                  )}
                  <Icon size={11} className={it.ok ? "text-emerald-400" : "text-zinc-500"} />
                  <span className="truncate">{it.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1">
          {!ready && (
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs h-9 font-semibold"
              data-testid="lifecycle-complete-setup-btn"
            >
              <MagicWand size={12} weight="fill" className="mr-1" />
              Complete Setup
            </Button>
          )}
          {ready && (
            <Button
              onClick={onActivate}
              disabled={activating}
              className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs h-9 font-semibold"
              data-testid="lifecycle-activate-btn"
            >
              <Lightning size={12} weight="fill" className="mr-1" />
              {activating ? "Activating…" : "Activate Monitoring"}
            </Button>
          )}
          {!ready && (
            <span className="text-[9px] text-zinc-500 mt-0.5">
              {readiness?.items?.filter((i) => !i.ok).length || 0} item(s) remaining
            </span>
          )}
        </div>
      </div>

      {/* Activation Wizard (Phase 48) */}
      {wizardOpen && (
        <ActivationWizard
          shipment={shipment}
          onClose={() => setWizardOpen(false)}
          onActivated={(newState) => {
            setWizardOpen(false);
            onActivated?.(newState);
            fetchReadiness();
          }}
        />
      )}
    </Card>
  );
}
